/**
 * The six stages an order passes through, and where a given status puts it.
 *
 * This is the data behind the stage strip at the top of the order timeline.
 * It is the old stepper's logic (TimeLine.vue's updateOrderStatusStep) lifted
 * out unchanged so it can be tested on its own: a status makes N stages
 * "done", the next one is "in process", the rest are "waiting". Nothing
 * about which status lands where has changed — this work is the strip's
 * appearance only.
 */

export type StageKey =
  | 'created'
  | 'artwork'
  | 'sample'
  | 'production'
  | 'shipped'
  | 'completed';

export type StageState = 'done' | 'current' | 'waiting';

export interface OrderStage {
  key: StageKey;
  label: string;
  state: StageState;
  /** The line under the label: Complete / In process… / Waiting… */
  detail: string;
}

export const STAGES: ReadonlyArray<{ key: StageKey; label: string }> = [
  { key: 'created', label: 'Order Created' },
  { key: 'artwork', label: 'Artwork Approval' },
  { key: 'sample', label: 'Sample Design' },
  { key: 'production', label: 'In Production' },
  { key: 'shipped', label: 'Order Shipped' },
  { key: 'completed', label: 'Order Completed' },
];

const DETAIL: Record<StageState, string> = {
  done: 'Complete',
  current: 'In process…',
  waiting: 'Waiting…',
};

/** How many stages a status has completed — the old stepper's currentStep, unchanged. */
export const stagesDone = (status: string): number => {
  switch ((status || '').toLowerCase()) {
    case 'factory_approved':
      return 2;
    case 'submitted_for_customer_review':
    case 'customer_approved':
    case 'customer_rejected':
      return 3;
    case 'in_production':
    case 'production_files_uploaded':
      return 4;
    case 'shipped':
      return 5;
    case 'completed':
      return 6;
    default:
      // submitted_for_factory_review, order_approve, order_cancel, the quote
      // statuses, pending_for_factory_assignment, factory_rejected, on_hold,
      // and anything unknown: the first stage, as before.
      return 1;
  }
};

export const orderStages = (status: string): OrderStage[] => {
  const done = stagesDone(status);

  return STAGES.map((stage, index) => {
    const state: StageState = index < done ? 'done' : index === done ? 'current' : 'waiting';
    return { ...stage, state, detail: DETAIL[state] };
  });
};
